import { useState } from "react";

export type BrandLogoSize = "small" | "medium" | "large";

const DEFAULT_SIZE_CLASSES: Record<BrandLogoSize, string> = {
  small: "max-h-8 max-w-[90px]",
  medium: "max-h-10 max-w-[120px]",
  large: "max-h-14 max-w-[170px]",
};

interface BrandingDisplayProps {
  logoUrl?: string | null;
  companyName: string;
  showCompanyName?: boolean | null;
  logoSize?: BrandLogoSize | null;
  className?: string;
  imageClassName?: string;
  sizeClasses?: Partial<Record<BrandLogoSize, string>>;
  nameClassName?: string;
  imageTestId?: string;
  nameTestId?: string;
}

/**
 * Keeps the company name visible while a logo is pending or has failed.
 * A builder's "hide name" preference only takes effect after the image loads.
 */
export function BrandingDisplay({
  logoUrl,
  companyName,
  showCompanyName = true,
  logoSize = "medium",
  className = "flex flex-col items-start gap-1",
  imageClassName = "",
  sizeClasses,
  nameClassName = "font-semibold",
  imageTestId = "img-brand-logo",
  nameTestId = "text-brand-name",
}: BrandingDisplayProps) {
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const logoLoaded = !!logoUrl && loadedUrl === logoUrl;
  const logoFailed = !!logoUrl && failedUrl === logoUrl;
  const shouldShowName = showCompanyName !== false || !logoLoaded;
  const selectedSize = logoSize || "medium";
  const sizeClassName = sizeClasses?.[selectedSize] || DEFAULT_SIZE_CLASSES[selectedSize];

  return (
    <div className={className}>
      {logoUrl && !logoFailed && (
        <img
          src={logoUrl}
          alt={`${companyName} logo`}
          className={`${sizeClassName} w-auto object-contain ${imageClassName}`}
          onLoad={() => {
            setFailedUrl(null);
            setLoadedUrl(logoUrl);
          }}
          onError={() => {
            setLoadedUrl(null);
            setFailedUrl(logoUrl);
          }}
          data-testid={imageTestId}
        />
      )}
      {shouldShowName && (
        <div className={nameClassName} data-testid={nameTestId}>{companyName}</div>
      )}
    </div>
  );
}